import { Capacitor } from "@capacitor/core";
import { Suspense, lazy, useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  HashRouter,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useAuthStore } from "./stores/authStore";
import { useThemeStore } from "./stores/themeStore";
import { useDesktopMenuNavigation } from "./hooks/useDesktopMenuNavigation";
import "./App.css";
import { NativeDebugPanel } from "./components/NativeDebugPanel";
import { FixedNavigation } from "./components/FixedNavigation";
import { ComponentErrorBoundary } from "./components/ComponentErrorBoundary";
import { DesktopLicenseGate } from "./components/DesktopLicenseGate";

const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })),
);
const CheckoutPage = lazy(() =>
  import("./pages/CheckoutPage").then((module) => ({
    default: module.CheckoutPage,
  })),
);
const ReportsPage = lazy(() =>
  import("./pages/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);
const AccountingLandingPage = lazy(() =>
  import("./pages/AccountingLandingPage").then((module) => ({
    default: module.AccountingLandingPage,
  })),
);
const AccountingReportsPage = lazy(() =>
  import("./pages/AccountingReportsPage").then((module) => ({
    default: module.AccountingReportsPage,
  })),
);
const AccountingAccountsPage = lazy(() =>
  import("./pages/AccountingAccountsPage").then((module) => ({
    default: module.AccountingAccountsPage,
  })),
);
const AccountingMappingsPage = lazy(() =>
  import("./pages/AccountingMappingsPage").then((module) => ({
    default: module.AccountingMappingsPage,
  })),
);
const AccountingJournalsPage = lazy(() =>
  import("./pages/AccountingJournalsPage").then((module) => ({
    default: module.AccountingJournalsPage,
  })),
);
const AccountingTaxRulesPage = lazy(() =>
  import("./pages/AccountingTaxRulesPage").then((module) => ({
    default: module.AccountingTaxRulesPage,
  })),
);
const AccountingTaxPeriodsPage = lazy(() =>
  import("./pages/AccountingTaxPeriodsPage").then((module) => ({
    default: module.AccountingTaxPeriodsPage,
  })),
);
const AccountingJournalDetailPage = lazy(() =>
  import("./pages/AccountingJournalDetailPage").then((module) => ({
    default: module.AccountingJournalDetailPage,
  })),
);
const InventoryManagementPage = lazy(() =>
  import("./pages/InventoryManagementPage").then((module) => ({
    default: module.InventoryManagementPage,
  })),
);
const AddInventoryPage = lazy(() =>
  import("./pages/AddInventoryPage").then((module) => ({
    default: module.AddInventoryPage,
  })),
);
const SuppliersPage = lazy(() =>
  import("./pages/SuppliersPage").then((module) => ({
    default: module.SuppliersPage,
  })),
);
const PurchaseOrdersPage = lazy(() =>
  import("./pages/PurchaseOrdersPage").then((module) => ({
    default: module.PurchaseOrdersPage,
  })),
);
const GRNPage = lazy(() =>
  import("./pages/GRNPage").then((module) => ({ default: module.GRNPage })),
);
const CustomersPage = lazy(() =>
  import("./pages/CustomersPage").then((module) => ({
    default: module.CustomersPage,
  })),
);
const ReturnsPage = lazy(() =>
  import("./pages/ReturnsPage").then((module) => ({
    default: module.ReturnsPage,
  })),
);
const CreditOrdersPage = lazy(() =>
  import("./pages/CreditOrdersPage").then((module) => ({
    default: module.CreditOrdersPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const SuperAdminPage = lazy(() =>
  import("./pages/SuperAdminPage").then((module) => ({
    default: module.SuperAdminPage,
  })),
);
const HomePage = lazy(() =>
  import("./pages/HomePage").then((module) => ({ default: module.HomePage })),
);
const GetAppPage = lazy(() =>
  import("./pages/GetAppPage").then((module) => ({
    default: module.GetAppPage,
  })),
);
const ExecutiveDashboardPage = lazy(() =>
  import("./pages/ExecutiveDashboardPage").then((module) => ({
    default: module.ExecutiveDashboardPage,
  })),
);
const BillingPage = lazy(() =>
  import("./pages/BillingPage").then((module) => ({
    default: module.BillingPage,
  })),
);
const AuditLogsPage = lazy(() =>
  import("./pages/AuditLogsPage").then((module) => ({
    default: module.AuditLogsPage,
  })),
);
const HelpSupportPage = lazy(() =>
  import("./pages/HelpSupportPage").then((module) => ({
    default: module.HelpSupportPage,
  })),
);
const SubscriptionPaymentCallbackPage = lazy(() =>
  import("./pages/SubscriptionPaymentCallbackPage").then((module) => ({
    default: module.SubscriptionPaymentCallbackPage,
  })),
);
const SplashPage = lazy(() =>
  import("./pages/SplashPage").then((module) => ({
    default: module.SplashPage,
  })),
);

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-xs font-semibold uppercase tracking-[0.6em] text-slate-400">
      Loading
    </div>
  );
}

function App() {
  const { isAuthenticated, user } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    user: state.user,
  }));
  const theme = useThemeStore((state) => state.theme);

  // Log app initialization
  console.log("[App] Initializing App component");
  console.log("[App] isAuthenticated:", isAuthenticated);
  console.log("[App] user:", user);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isCashier = user?.role === "cashier";
  const isPlatformAdmin = Boolean(user?.isPlatformAdmin);
  const isCompanyUser = isAuthenticated && !isPlatformAdmin;

  // Determine landing path based on role
  const authenticatedLandingPath = isPlatformAdmin
    ? "/superadmin"
    : isAdmin
      ? "/dashboard"
      : isManager
        ? "/checkout"
        : "/checkout"; // Cashiers go to checkout
  const isElectron =
    typeof navigator !== "undefined" &&
    navigator.userAgent.toLowerCase().includes("electron");
  const isNativePlatform = isElectron || Capacitor.getPlatform() !== "web";

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = theme;
    }
  }, [theme]);

  const Router = useMemo(() => {
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent.toLowerCase().includes("electron")
    ) {
      return HashRouter;
    }
    return BrowserRouter;
  }, []);

  console.log("[App] Before render:");
  console.log("[App] Router type:", Router.name);
  console.log("[App] isAuthenticated:", isAuthenticated);
  console.log("[App] isNativePlatform:", isNativePlatform);

  const RouterContent = () => {
    // Enable desktop menu navigation when running in Electron (must be inside Router)
    useDesktopMenuNavigation();

    return (
      <div className="app">
        <ComponentErrorBoundary name="FixedNavigation">
          <FixedNavigation />
        </ComponentErrorBoundary>
        <ComponentErrorBoundary name="NativeDebugPanel">
          <NativeDebugPanel />
        </ComponentErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to={authenticatedLandingPath} replace />
                ) : isNativePlatform ? (
                  <SplashPage />
                ) : (
                  <HomePage />
                )
              }
            />

            <Route
              path="/audit-logs"
              element={
                isCompanyUser && (isAdmin || isManager) ? (
                  <AuditLogsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/help"
              element={
                isCompanyUser ? (
                  <HelpSupportPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to={authenticatedLandingPath} replace />
                ) : (
                  <LoginPage />
                )
              }
            />
            <Route
              path="/:tenantSlug/login"
              element={
                isAuthenticated ? (
                  <Navigate to={authenticatedLandingPath} replace />
                ) : (
                  <LoginPage />
                )
              }
            />
            <Route path="/get-app" element={<GetAppPage />} />
            <Route
              path="/subscription/payment-callback"
              element={<SubscriptionPaymentCallbackPage />}
            />
            <Route
              path="/checkout"
              element={
                isCompanyUser ? (
                  <CheckoutPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                isCompanyUser && (isAdmin || isManager) ? (
                  <ExecutiveDashboardPage />
                ) : isCashier ? (
                  <Navigate to="/checkout" replace />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/reports"
              element={
                isCompanyUser && (isAdmin || isManager) ? (
                  <ReportsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/accounting"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingLandingPage />
                ) : isCompanyUser && isManager ? (
                  <Navigate to="/accounting/reports" replace />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/reports"
              element={
                isCompanyUser && (isAdmin || isManager) ? (
                  <AccountingReportsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/accounts"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingAccountsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/mappings"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingMappingsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/journals"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingJournalsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/journals/:id"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingJournalDetailPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/tax-rules"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingTaxRulesPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/accounting/tax-periods"
              element={
                isCompanyUser && isAdmin ? (
                  <AccountingTaxPeriodsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/inventory"
              element={
                isCompanyUser && isAdmin ? (
                  <AddInventoryPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/inventory-management"
              element={
                isCompanyUser && isAdmin ? (
                  <InventoryManagementPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/suppliers"
              element={
                isCompanyUser && isAdmin ? (
                  <SuppliersPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/purchase-orders"
              element={
                isCompanyUser && isAdmin ? (
                  <PurchaseOrdersPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/grn"
              element={
                isCompanyUser && isAdmin ? (
                  <GRNPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/customers"
              element={
                isCompanyUser && isAdmin ? (
                  <CustomersPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/returns"
              element={
                isCompanyUser && isAdmin ? (
                  <ReturnsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/credit-orders"
              element={
                isCompanyUser && (isAdmin || isManager) ? (
                  <CreditOrdersPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                isCompanyUser && isAdmin ? (
                  <SettingsPage />
                ) : isPlatformAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/superadmin"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <Navigate to="/superadmin/dashboard" replace />
                ) : (
                  <Navigate to="/superadmin/login" replace />
                )
              }
            />
            <Route
              path="/superadmin/login"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <Navigate to="/superadmin/dashboard" replace />
                ) : (
                  <LoginPage variant="superadmin" />
                )
              }
            />
            <Route
              path="/superadmin/dashboard"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <SuperAdminPage />
                ) : (
                  <Navigate to="/superadmin/login" replace />
                )
              }
            />
            <Route
              path="/superadmin/billing"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <BillingPage />
                ) : (
                  <Navigate to="/superadmin/login" replace />
                )
              }
            />
            <Route
              path="/admin"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <Navigate to="/superadmin/dashboard" replace />
                ) : (
                  <Navigate to="/superadmin/login" replace />
                )
              }
            />
            <Route
              path="/admin/tenants"
              element={
                isAuthenticated && isPlatformAdmin ? (
                  <Navigate to="/superadmin/dashboard" replace />
                ) : (
                  <Navigate to="/superadmin/login" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </div>
    );
  };

  const appContent = (
    <Router>
      <RouterContent />
    </Router>
  );

  if (isElectron) {
    return <DesktopLicenseGate>{appContent}</DesktopLicenseGate>;
  }

  return appContent;
}

export default App;
